import React, { useState, useEffect, useRef } from "react";
import styles from "./TableInput.module.scss";
import { ReactComponent as EditIcon } from "../../icons/blue-edit.svg";

const TableInput = ({
  id,
  initialValue,
  onSave,
  placeholder = "Not defined",
  canEdit = true,
  className = "",
}) => {
  const [value, setValue] = useState(initialValue || placeholder);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    // Update value if initialValue changes from parent
    if (!isEditing) {
      setValue(initialValue || placeholder);
    }
  }, [initialValue, placeholder, isEditing]);

  useEffect(() => {
    // Focus the input when editing begins
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  // Handle focus on input
  const handleFocus = () => {
    setIsEditing(true);
  };

  // Handle blur on input
  const handleBlur = () => {
    setIsEditing(false);
    onSave(value); // Save when focus is lost
  };

  // Handle key press on input
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      setIsEditing(false);
      onSave(value);
      inputRef.current.blur();
    }
  };

  // Handle edit icon click
  const handleEditClick = (e) => {
    e.stopPropagation(); // Prevent event bubbling
    setIsEditing(true);
    // setTimeout to ensure the DOM is updated before focusing
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  };

  // Handle cell click to enter edit mode
  const handleCellClick = () => {
    if (canEdit && !isEditing) {
      setIsEditing(true);
    }
  };

  if (!canEdit) {
    return (
      <div className={`${styles.readOnlyCell} ${className}`}>
        {initialValue || placeholder}
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className={styles.inputWrapper} ref={containerRef}>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={`${styles.tableInput} ${className}`}
          autoFocus
        />
      </div>
    );
  } else {
    return (
      <div
        className={`${styles.inputContainer} ${className}`}
        onClick={handleCellClick}
      >
        <div className={styles.readOnlyCell}>{value}</div>
        <EditIcon className={styles.editIcon} onClick={handleEditClick} />
      </div>
    );
  }
};

export default TableInput;
